import React, { useCallback, useRef, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { EMAIL_PREFERENCE } from 'helpers/constants/cart';
import useClassNames from 'helpers/hooks/useClassNames';
import useControllableState from 'helpers/hooks/useControllable';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop } from 'helpers/utils/screensizes';
import { cleanString } from 'helpers/utils/util';

export interface CheckboxProps extends Omit<React.ComponentProps<'input'>, 'onChange' | 'key'> {
  label?: React.ReactNode | string;
  labelPosition?: 'on-left' | 'on-right';
  containerClassName?: string;
  onChange?: (props: { name: string; checked: boolean }) => void;
  disableBackground?: boolean;
  checkedBgColor?: string; // Tailwind class or custom color
  checkedIconColor?: string;
  isType?: string;
  showTickOnHover?: boolean;
  dataffid?: string;
}

const Checkbox: React.FC<CheckboxProps> = ({
  className = '',
  checked,
  onChange,
  onMouseOver,
  onMouseLeave,
  onFocus,
  containerClassName,
  label,
  defaultChecked = false,
  labelPosition = 'on-right',
  disableBackground = false,
  checkedBgColor = 'bg-green-800',
  checkedIconColor = 'text-white',
  isType,
  showTickOnHover = true,
  dataffid,
  ...props
}) => {
  const labelString = typeof label === 'string' ? label : '';

  const [isHovered, setIsHovered] = useState(false);
  const [isChecked, setIsChecked] = useControllableState(checked, defaultChecked);

  const [isDesktopSize] = useMediaQuery(desktop);
  const checkboxRef = useRef<HTMLInputElement>(null);

  const toggleIsChecked = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newChecked = e.target.checked;

      onChange?.({
        name: props.name ?? '',
        checked: newChecked,
      });

      if (checked === undefined) {
        setIsChecked(newChecked);
      }
    },
    [onChange, props.name, checked, setIsChecked],
  );

  const handleMouseOver = (e: React.MouseEvent<HTMLInputElement>) => {
    setIsHovered(isDesktopSize);
    onMouseOver?.(e);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsHovered(false);
    onFocus?.(e);
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLInputElement>) => {
    setIsHovered(false);
    onMouseLeave?.(e);
  };

  const containerClassNames = useClassNames([
    'appearance-none border-none bg-transparent cursor-pointer text-left',
    'flex gap-12',
    containerClassName,
  ]);

  const buttonClassName = useClassNames([
    'h-20 w-20 min-w-[20px] rounded-sm relative cursor-pointer',
    isHovered ? 'outline-gray-600' : 'outline-neutral-400',
  ]);

  const inputClassName = useClassNames([
    'absolute w-full h-full z-[2] rounded-sm border-2 text-transparent bg-transparent',
    'border-[#333333]',
    'checked:bg-none',
    'focus:outline focus:outline-2 focus:outline-[#333333]',
    'focus:checked:outline-[#333333]',
    className,
  ]);

  const iconClassName = useClassNames([
    'absolute top-[50%] z-[1] left-[50%] h-16 w-16 translate-y-[-50%] translate-x-[-50%] stroke-[2px]',
    isChecked || (isHovered && showTickOnHover) ? 'block' : 'hidden',
    isChecked && !disableBackground ? checkedIconColor : 'text-[#333333]',
  ]);

  const backgroundClassName = useClassNames(['absolute z-0 h-20 w-20 rounded-sm', { [checkedBgColor]: !!isChecked }]);

  const id = `chk-${cleanString(labelString)}`;
  const labelId = `lbl-${cleanString(labelString)}`;

  const labelTextCalss =
    isType === EMAIL_PREFERENCE
      ? 'text-14 md:text-16 text-[#333333] cursor-pointer'
      : 'text-14 text-gray-600 cursor-pointer';

  const LabelText =
    typeof label === 'string' ? (
      <span id={labelId} data-ffid={`lbl-filter-text-${cleanString(labelString)}`} className={labelTextCalss}>
        {label}
      </span>
    ) : (
      label
    );

  return (
    <label className={containerClassNames} data-ffid={dataffid}>
      {label && labelPosition === 'on-left' && LabelText}

      <div className={buttonClassName}>
        <input
          id={id}
          data-ffid={`chk-box-custom-${cleanString(labelString)}`}
          ref={checkboxRef}
          type="checkbox"
          aria-labelledby={props['aria-labelledby'] ?? labelId}
          checked={isChecked}
          className={inputClassName}
          onMouseOver={handleMouseOver}
          onFocus={handleFocus}
          onMouseLeave={handleMouseLeave}
          onChange={toggleIsChecked}
          onClick={(e) => {
            const checkbox = e.currentTarget;
            setTimeout(() => {
              checkbox.blur();
            }, 100);
          }}
          onMouseDown={(e) => e.preventDefault()}
          onTouchStart={(e) => e.preventDefault()}
          {...props}
    />

        <CheckIcon className={iconClassName} />

        {!disableBackground && <div className={backgroundClassName} />}
      </div>

      {label && labelPosition === 'on-right' && LabelText}
    </label>
  );
};

export default Checkbox;
